import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Account } from '../interface/account';
import { Application } from '../interface/application';
import { GenericPaginatedList } from '../interface/generic-paginated-list';
import { Metatag } from '../interface/metatag';
import { Report } from '../interface/report';
import { ApiService } from '../services/api.service';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  constructor(private api: ApiService) {}

  getAccount(
    accountId: string,
    includeExtension: boolean = false,
  ): Observable<Account> {
    const parameters = {
      includeExtension,
    };
    return this.api.one('accounts', accountId).get<Account>(parameters);
  }

  getAccounts(
    itemsPerPage: number = 10,
    page: number = 0,
    searchLegalName: string = '',
    order: string = 'ASC',
    columnName: string = 'legalName',
    showSubAccounts: boolean = true,
    includeExtension: boolean = false,
    isInternalOnly?: boolean,
  ): Observable<GenericPaginatedList<Account>> {
    const parameters: any = {
      size: itemsPerPage,
      page: page,
      order: order,
      columnName: columnName,
      showSubAccounts: showSubAccounts,
      legalName: searchLegalName,
      includeExtension,
    };
    if (isInternalOnly !== undefined && isInternalOnly !== null) {
      parameters.isInternalOnly = isInternalOnly;
    }
    return this.api
      .all('accounts')
      .get<GenericPaginatedList<Account>>(parameters)
      .pipe(
        catchError((error) => {
          console.error('Error fetching accounts', {
            parameters,
            error,
          });
          const emptyPage: GenericPaginatedList<Account> = {
            content: [],
            number: 0,
            numberOfElements: 0,
            totalElements: 0,
            totalPages: 0,
            size: 0,
            sort: [],
            first: true,
            last: true,
          };
          return of(emptyPage);
        }),
      );
  }

  getAll(params?: any): Observable<GenericPaginatedList<Account>> {
    return this.api.all('accounts').get<GenericPaginatedList<Account>>(params);
  }

  getByLegalName(legalName: string, params?: any) {
    return this.api.one('accounts/legalname', legalName).get<Account>(params);
  }

  getByNymityId(nymityId: string, params?: any) {
    return this.api.one('accounts/nymityid', nymityId).get<Account>(params);
  }

  add(account: Account, includeExtension: boolean = false) {
    const params = {
      includeExtension,
    };
    return this.api.post<Account>('accounts', account, params);
  }

  update(
    accountId: string,
    account: Account,
    includeExtension: boolean = false,
  ) {
    const params = {
      includeExtension,
    };
    return this.api.put<Account>(`accounts/${accountId}`, account, params);
  }

  enable(accountId: string): Observable<Account> {
    return this.api.post<Account>(`accounts/${accountId}/enable`);
  }

  disable(accountId: string): Observable<Account> {
    return this.api.post<Account>(`accounts/${accountId}/disable`);
  }

  emailActivations(accountId: string) {
    return this.api.post(`accounts/${accountId}/activations`);
  }

  getMetatags(
    accountId: string,
    required: boolean = false,
    page: number = 0,
    size: number = 50,
  ): Observable<GenericPaginatedList<Metatag>> {
    const params = { page, size, required };
    return this.api
      .all(`accounts/${accountId}/metatags`)
      .get<GenericPaginatedList<Metatag>>(params);
  }

  downloadUser(params: {
    accountId: string;
    applications: Application[];
  }): Observable<Report> {
    return this.api.get<Report>(`accounts/${params.accountId}/users/download`, {
      applications: params.applications.map((app) => app.clientId),
    });
  }
}
